import type { PayloadAction } from "@reduxjs/toolkit";
import type { EditorRootState } from "@v1/store/store";
import type { DragType } from "@v1/types/drag-type";
import type { DropTarget } from "@v1/types/drop-target";

import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  selectComponentDataMapping,
  selectDraftComponentIds,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
} from "@v1/reducers/core-reducer";

export type DragAndDropState = {
  // Type of current drag, e.g. existing component, new template, etc
  currentDragType: DragType | null;
  // Unique identifier of the thing being dragged
  currentDraggingIdentifier: string | null;
  // Current drop target in canvas
  dropTarget: DropTarget;
  // Whether we're dragging spacing indicators (padding, margin) or not
  isDraggingSpacingIndicators: boolean;
  // Cursor which should be shown everywhere in the app during drag
  draggingCursor?: string;
};

const initialState: DragAndDropState = {
  currentDragType: null,
  currentDraggingIdentifier: null,
  dropTarget: { edge: null, error: null, componentId: null },
  isDraggingSpacingIndicators: false,
};

const dragAndDropSlice = createSlice({
  name: "dragAndDrop",
  initialState,
  reducers: {
    setCurrentDragTypeAndIdentifier: (
      state,
      action: PayloadAction<{
        dragType: DragType | null;
        identifier: string | null;
      }>,
    ) => {
      state.currentDragType = action.payload.dragType;
      state.currentDraggingIdentifier = action.payload.identifier;
    },
    setDropTarget: (state, action: PayloadAction<DropTarget>) => {
      state.dropTarget = action.payload;
    },
    setDraggingCursor: (state, action: PayloadAction<string | null>) => {
      state.draggingCursor = action.payload ?? undefined;
    },
  },
});

export const selectIsDraggingSpacingIndicators = (state: EditorRootState) => {
  return state.dragAndDrop.isDraggingSpacingIndicators;
};

export const selectCurrentDragType = (state: EditorRootState) => {
  return state.dragAndDrop.currentDragType;
};

export const selectCurrentDraggingIdentifier = (state: EditorRootState) => {
  return state.dragAndDrop.currentDraggingIdentifier;
};

/** Given the currently selected draftComponent this selector returns which
 * components should be selectable:
 * - Root component (always selectable)
 * - Top level children of the root component (always selectable)
 * - Draft component
 * - Top level children of the draft component
 * - Top level children of the draft component's parent
 * - Ancestors of the draft component
 *  */
export const selectSelectableComponentIds = createSelector(
  selectComponentDataMapping,
  selectDraftComponentIds,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  (componentDataMapping, draftComponentIds, draftElement) => {
    const selectableComponentIds = new Set<string>();
    // TODO (Gabe 2025-04-09): do we need to do anything for multi-select?
    const rootComponentId = draftElement?.component.id;

    if (!rootComponentId) {
      return selectableComponentIds;
    }
    selectableComponentIds.add(rootComponentId);

    const rootComponentData = componentDataMapping[rootComponentId];
    if (!rootComponentData) {
      return selectableComponentIds;
    }
    const rootTopLevelChildrenIds =
      rootComponentData.containedComponentData
        ?.filter(([, , level]) => level === 1)
        .map(([id]) => id) ?? [];
    rootTopLevelChildrenIds.forEach((id) => selectableComponentIds.add(id));

    const draftComponentId = draftComponentIds[0];
    if (!draftComponentId) {
      return selectableComponentIds;
    }
    selectableComponentIds.add(draftComponentId);

    const draftComponentData = componentDataMapping[draftComponentId];
    if (!draftComponentData) {
      return selectableComponentIds;
    }
    const draftComponentChildrenIds =
      draftComponentData.containedComponentData
        ?.filter(([, , level]) => level === 1)
        .map(([id]) => id) ?? [];
    draftComponentChildrenIds.forEach((id) => selectableComponentIds.add(id));

    const draftComponentParentId = draftComponentData.parentId;
    if (!draftComponentParentId) {
      return selectableComponentIds;
    }

    const draftComponentParentData =
      componentDataMapping[draftComponentParentId];
    if (!draftComponentParentData) {
      return selectableComponentIds;
    }
    const draftComponentParentChildrenIds =
      draftComponentParentData.containedComponentData
        ?.filter(([, , level]) => level === 1)
        .map(([id]) => id) ?? [];
    draftComponentParentChildrenIds.forEach((id) => {
      return selectableComponentIds.add(id);
    });

    const draftComponentAncestorsIds =
      draftComponentData.ancestorComponentData?.map(([id]) => id) ?? [];
    draftComponentAncestorsIds.forEach((id) => selectableComponentIds.add(id));

    return selectableComponentIds;
  },
);

const { actions, reducer } = dragAndDropSlice;

export const {
  setCurrentDragTypeAndIdentifier,
  setDropTarget,
  setDraggingCursor,
} = actions;
export default reducer;
